// `compileFlow()` — trasforma il JSON di un flow in una `WizardDef` eseguibile
// dal `WizardModal` già esistente.
//
// È il punto in cui il sistema configurabile si innesta su quello a codice:
// il motore di navigazione, la history, lo spinner e il commit finale restano
// quelli di `WizardModal.vue`. Qui si sostituiscono solo le closure TS
// (`validate`, `next`, `onNext`, `apply`) con le loro versioni dichiarative.
//
// Un `WizardDef` prodotto da qui è indistinguibile, per il modale, da uno
// scritto a mano: i due sistemi possono convivere durante la migrazione.

import { markRaw } from 'vue'
import type { WizardCtx, WizardDef, WizardStep } from '../types'
import FlowFormStep from '../steps/FlowFormStep.vue'
import type {
  FlowActionSpec, FlowDefinition, FlowEdge, FlowNode, FlowScope,
} from './flowTypes'
import { FLOW_END } from './flowTypes'
import { collectConditionIssues, evaluateCondition } from './conditions'
import { collectActionIssues, executeFlowActions, type FlowActionContext } from './actions'
import { getFlowControl } from './controls'
import { collectVarSources, deriveFlowVars, eFromIniziale, normalizeVarName } from './vars'

export interface CompileFlowOptions {
  /**
   * Valori aggiuntivi dello scope, riletti a ogni valutazione (così `setting`,
   * `user`, `env` e `inputs` restano aggiornati).
   * Tipicamente: `() => ({ setting, user, env, inputs })`.
   */
  scope?: () => Partial<FlowScope>
  /**
   * Valori degli `inputs` dichiarati dal flow, in forma FISSA.
   *
   * È una fotografia scattata al momento della compilazione: va bene per chi
   * compila con parametri già noti e immutabili (l'anteprima del builder), NON
   * per una pagina i cui dati arrivano dopo. Un flow compilato all'avvio con
   * `inputs` presi da una `computed` ancora vuota li terrebbe vuoti per
   * sempre — succedeva con la targa e il chilometraggio, letti da una chiamata
   * che arriva più tardi.
   *
   * Chi ha valori che cambiano li passa dentro `scope()`, che vince su questo.
   */
  inputs?: Record<string, unknown>
}

// ─── Scope ────────────────────────────────────────────────────────────────────

/**
 * Costruisce lo scope da un `WizardCtx`. Funzione PURA: viene invocata anche
 * dentro `props()` durante il render, dove un side effect romperebbe la
 * reattività (vedi il commento in `WizardModal.vue`).
 */
export function buildFlowScope(
  ctx: WizardCtx,
  options: CompileFlowOptions = {},
  answer?: unknown,
): FlowScope {
  const helpers = (ctx.helpers ?? {}) as Record<string, unknown>
  const extra = options.scope?.() ?? {}

  return {
    item: ctx.item,
    answers: ctx.answers,
    // Derivate a monte dal motore (`useWizard`), che è l'unico a conoscere il
    // percorso attraversato. Qui si trasportano soltanto.
    vars: (ctx.vars as Record<string, unknown> | undefined) ?? {},
    // `scope()` prima di `options.inputs`: il primo si rilegge a ogni
    // valutazione, il secondo è fermo alla compilazione.
    inputs: extra.inputs ?? options.inputs ?? {},
    // `setting`/`user` possono arrivare come ref: `getScopeValue` li srotola.
    setting: extra.setting ?? ctx.setting ?? helpers.setting,
    user: extra.user ?? ctx.user ?? helpers.user,
    env: extra.env ?? {},
    original: extra.original ?? (ctx.original as Record<string, unknown> | undefined),
    changes: extra.changes ?? (ctx.changes as Record<string, unknown> | undefined),
    $answer: answer,
  }
}

export function buildFlowActionContext(
  flow: FlowDefinition,
  ctx: WizardCtx,
  options: CompileFlowOptions = {},
  node?: FlowNode,
  answer?: unknown,
): FlowActionContext {
  return {
    item: ctx.item,
    answers: ctx.answers,
    scope: buildFlowScope(ctx, options, answer),
    helpers: (ctx.helpers ?? {}) as Record<string, unknown>,
    flow,
    node,
  }
}

/** Esegue `on_cancel`: il modale non ha un hook per l'annullamento. */
export async function executeFlowCancel(
  flow: FlowDefinition,
  ctx: WizardCtx,
  options: CompileFlowOptions = {},
): Promise<void> {
  await executeFlowActions(flow.on_cancel, buildFlowActionContext(flow, ctx, options))
}

// ─── Obbligatorietà dei campi ─────────────────────────────────────────────────

/**
 * Un campo obbligatorio è "non compilato" se vale null/undefined/''/[]/false.
 * Lo `0` NON è considerato mancante (una quantità zero può essere una risposta
 * legittima). Volutamente NON si riusa l'operatore `empty` del DSL, che invece
 * considera vuoto anche `0`.
 */
function isMissing(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (value === '') return true
  if (value === false) return true
  if (Array.isArray(value)) return value.length === 0
  return false
}

// ─── Compilazione ─────────────────────────────────────────────────────────────

export function compileFlow(flow: FlowDefinition, options: CompileFlowOptions = {}): WizardDef {
  const nodes = flow.nodes ?? []
  const edgesByFrom = new Map<string, FlowEdge[]>()
  for (const edge of flow.edges ?? []) {
    const list = edgesByFrom.get(edge.from)
    if (list) list.push(edge)
    else edgesByFrom.set(edge.from, [edge])
  }

  /** Prossimo nodo in ordine di array, saltando quelli con `skip_when` vero. */
  const linearNext = (fromId: string, ctx: WizardCtx): string | null => {
    const index = nodes.findIndex((n) => n.id === fromId)
    for (let i = index + 1; i < nodes.length; i++) {
      const candidate = nodes[i]
      if (candidate.skip_when && evaluateCondition(candidate.skip_when, buildFlowScope(ctx, options))) {
        continue
      }
      return candidate.id
    }
    return null
  }

  /**
   * Risoluzione dell'arco uscente: vince il PRIMO arco senza condizione o con
   * condizione vera. Se il nodo ha archi ma nessuno è soddisfatto si ricade sul
   * prossimo nodo in ordine, con un warning: terminare il wizard di soppiatto
   * sarebbe peggio (l'utente vedrebbe applicarsi `on_complete` senza motivo).
   */
  const resolveNext = (node: FlowNode, answer: unknown, ctx: WizardCtx): string | null => {
    const outgoing = edgesByFrom.get(node.id) ?? []
    const scope = buildFlowScope(ctx, options, answer)

    for (const edge of outgoing) {
      if (!edge.when || evaluateCondition(edge.when, scope)) {
        return edge.to === FLOW_END ? null : edge.to
      }
    }

    console.warn(
      `[flow/${flow.id}] nessun arco soddisfatto da '${node.id}': proseguo in ordine lineare`,
    )
    return linearNext(node.id, ctx)
  }

  const steps: WizardStep[] = nodes.map((node) => {
    const hasEdges = (edgesByFrom.get(node.id) ?? []).length > 0

    const step: WizardStep = {
      id: node.id,
      title: node.title,
      component: markRaw(FlowFormStep),
      props: (ctx) => ({
        node,
        scope: buildFlowScope(ctx, options, ctx.answers[node.id]),
      }),
      autoAdvance: node.auto_advance === true,
    }

    if (node.skip_when) {
      step.skip = (ctx) => evaluateCondition(node.skip_when, buildFlowScope(ctx, options))
    }

    const fields = node.fields ?? []
    const hasValidation = fields.some((f) => f.required) || (node.validate?.length ?? 0) > 0
    if (hasValidation) {
      step.validate = (answer, ctx) => {
        const scope = buildFlowScope(ctx, options, answer)
        const values = (answer ?? {}) as Record<string, unknown>

        for (const field of fields) {
          if (!field.required) continue
          const control = getFlowControl(field.control)
          if (control?.kind === 'display') continue
          if (field.visible_when && !evaluateCondition(field.visible_when, scope)) continue
          if (isMissing(values[field.name])) {
            return field.requiredMessage ?? `Compila "${field.label ?? field.name}".`
          }
        }

        for (const rule of node.validate ?? []) {
          if (!evaluateCondition(rule.require, scope)) return rule.message
        }

        return null
      }
    }

    if (node.on_next?.length) {
      step.onNext = async (answer, ctx) => {
        await executeFlowActions(
          node.on_next as FlowActionSpec[],
          buildFlowActionContext(flow, ctx, options, node, answer),
        )
      }
    }

    // `next`/`isTerminal` solo per i nodi con archi espliciti: senza archi il
    // modale usa la sua navigazione lineare, che già gestisce `skip` e l'etichetta
    // "Conferma" sull'ultimo passo.
    if (hasEdges) {
      step.next = (answer, ctx) => resolveNext(node, answer, ctx)
      step.isTerminal = (answer, ctx) => resolveNext(node, answer, ctx) === null
    }

    return step
  })

  return {
    id: flow.id,
    title: flow.title,
    steps,
    // `env` e `inputs` arrivano da qui e non dal `ctx`: le variabili iniziali
    // vanno risolte PRIMA che `vars` esista, e passare dal contesto — che
    // contiene già `vars` — chiuderebbe un anello.
    deriveVars: (answers, path) => {
      const extra = options.scope?.() ?? {}
      return deriveFlowVars(flow, answers, path, {
        env: (extra.env ?? {}) as Record<string, unknown>,
        inputs: extra.inputs ?? options.inputs ?? {},
      })
    },
    apply: async (_item, _answers, ctx) => {
      await executeFlowActions(
        flow.on_complete,
        buildFlowActionContext(flow, ctx, options),
      )
    },
  }
}

// ─── Validazione in fase di pubblicazione ─────────────────────────────────────

export interface FlowIssue {
  severity: 'error' | 'warning'
  path: string
  message: string
}

/**
 * Controlli statici su un flow. Da chiamare nel builder PRIMA di pubblicare:
 * qui si intercettano azioni non registrate, controlli inesistenti, archi
 * verso nodi cancellati, operatori inventati. A runtime è troppo tardi.
 */
export function validateFlow(flow: FlowDefinition): FlowIssue[] {
  const issues: FlowIssue[] = []
  const error = (path: string, message: string) => issues.push({ severity: 'error', path, message })
  const warn = (path: string, message: string) => issues.push({ severity: 'warning', path, message })

  if (!flow.id) error('id', 'manca `id`')

  const nodes = flow.nodes ?? []
  if (nodes.length === 0) {
    error('nodes', 'il flow non ha nodi')
    return issues
  }

  const ids = new Set<string>()
  for (const [i, node] of nodes.entries()) {
    const at = `nodes[${i}]`
    if (!node.id) {
      error(at, 'manca `id`')
      continue
    }
    if (ids.has(node.id)) error(at, `id duplicato '${node.id}'`)
    ids.add(node.id)

    const type = node.type ?? 'form'
    if (type === 'confirm') {
      if (!node.rows?.length) warn(`${at} (${node.id})`, "nodo 'confirm' senza righe da mostrare")
    } else {
      if (!node.fields?.length) warn(`${at} (${node.id})`, "nodo 'form' senza campi")
    }

    const fieldNames = new Set<string>()
    for (const [j, field] of (node.fields ?? []).entries()) {
      const fat = `${at}.fields[${j}]`
      if (!field.name) error(fat, 'manca `name`')
      else if (fieldNames.has(field.name)) error(fat, `nome campo duplicato '${field.name}'`)
      else fieldNames.add(field.name)

      const control = getFlowControl(field.control)
      if (!control) {
        error(fat, `controllo non registrato '${field.control}'`)
      } else if (control.needsOptions && !field.options && !field.optionsFrom) {
        error(fat, `il controllo '${field.control}' richiede \`options\` o \`optionsFrom\``)
      }

      issues.push(...conditionIssues(field.visible_when, `${fat}.visible_when`))
      issues.push(...conditionIssues(field.disabled_when, `${fat}.disabled_when`))
      issues.push(...conditionIssues(field.optionsWhere, `${fat}.optionsWhere`))

      if (field.optionsWhere && !control?.needsOptions) {
        warn(fat, `\`optionsWhere\` non ha effetto sul controllo '${field.control}': non usa opzioni`)
      }

      if (field.exportTo !== undefined) {
        if (normalizeVarName(field.exportTo) === null) {
          error(fat, `nome variabile non valido in \`exportTo\`: '${field.exportTo}'. `
            + 'Ammessi lettere, cifre e underscore, senza punti (es. statoRicezioneId)')
        } else if (control?.kind === 'display') {
          warn(fat, `\`exportTo\` su un controllo '${field.control}' che non raccoglie niente: `
            + 'la variabile resterebbe sempre vuota')
        }
      }
    }

    issues.push(...conditionIssues(node.skip_when, `${at}.skip_when`))
    for (const [j, rule] of (node.validate ?? []).entries()) {
      if (!rule.message) warn(`${at}.validate[${j}]`, 'regola senza messaggio')
      issues.push(...conditionIssues(rule.require, `${at}.validate[${j}].require`))
    }
    issues.push(...actionIssues(node.on_next, `${at}.on_next`))
  }

  // Archi
  const targeted = new Set<string>()
  for (const [i, edge] of (flow.edges ?? []).entries()) {
    const at = `edges[${i}]`
    if (!ids.has(edge.from)) error(at, `\`from\` punta a un nodo inesistente '${edge.from}'`)
    if (edge.to !== FLOW_END && !ids.has(edge.to)) {
      error(at, `\`to\` punta a un nodo inesistente '${edge.to}'`)
    }
    if (edge.to !== FLOW_END) targeted.add(edge.to)
    issues.push(...conditionIssues(edge.when, `${at}.when`))
  }

  // Raggiungibilità statica: un nodo è raggiungibile se è il primo, se un arco
  // lo punta, o se il nodo precedente in ordine non ha archi espliciti (e
  // quindi ricade sulla navigazione lineare).
  const hasOutgoing = new Set((flow.edges ?? []).map((e) => e.from))
  for (const [i, node] of nodes.entries()) {
    if (i === 0 || targeted.has(node.id)) continue
    if (!hasOutgoing.has(nodes[i - 1].id)) continue
    warn(`nodes[${i}] (${node.id})`, 'nodo non raggiungibile: nessun arco lo punta')
  }

  // Variabili iniziali: un `from` fuori da `env`/`inputs` non è un valore
  // iniziale, e resterebbe silenziosamente vuoto.
  for (const [i, seed] of (flow.vars ?? []).entries()) {
    const at = `vars[${i}]`
    if (normalizeVarName(seed?.name) === null) {
      error(at, `nome variabile non valido: '${String(seed?.name ?? '')}'`)
    }
    if (!eFromIniziale(seed?.from)) {
      error(at, `\`da\` non valido: '${String(seed?.from ?? '')}'. `
        + 'Una variabile iniziale può leggere solo `env.*` o `inputs.*`; '
        + "per il resto usa l'azione \"Imposta variabile\" su un passo.")
    }
  }

  // Variabili: una sola sorgente su un nodo saltabile è la trappola tipica —
  // il flow funziona finché si passa di lì, e il giorno che il nodo viene
  // saltato la variabile arriva vuota in fondo senza che nessuno se ne accorga.
  const varSources = collectVarSources(flow)
  const sourcesByName = new Map<string, typeof varSources>()
  for (const source of varSources) {
    const list = sourcesByName.get(source.name)
    if (list) list.push(source)
    else sourcesByName.set(source.name, [source])
  }
  for (const [name, sources] of sourcesByName) {
    if (sources.length > 1 || !sources[0].skippable) continue
    // Con un valore iniziale alle spalle il salto non lascia la variabile vuota.
    if (sources.some((s) => s.origine === 'iniziale')) continue
    warn(
      `vars.${name}`,
      `la variabile '${name}' è alimentata solo dal passo '${sources[0].nodeTitle}', `
      + 'che ha una condizione di salto: se viene saltato la variabile resta vuota',
    )
  }

  issues.push(...conditionIssues(flow.visible_when, 'visible_when'))
  issues.push(...actionIssues(flow.on_complete, 'on_complete'))
  issues.push(...actionIssues(flow.on_cancel, 'on_cancel'))

  if (!flow.on_complete?.length) {
    warn('on_complete', 'il flow non applica nulla al termine')
  }

  return issues

  function conditionIssues(condition: Parameters<typeof collectConditionIssues>[0], path: string): FlowIssue[] {
    return collectConditionIssues(condition, path).map((message) => ({
      severity: 'error' as const, path, message,
    }))
  }
  function actionIssues(actions: FlowActionSpec[] | undefined, path: string): FlowIssue[] {
    return collectActionIssues(actions, path).map((message) => ({
      severity: 'error' as const, path, message,
    }))
  }
}

/** Un flow è pubblicabile se non ha problemi di severità `error`. */
export function isFlowPublishable(flow: FlowDefinition): boolean {
  return !validateFlow(flow).some((i) => i.severity === 'error')
}
